import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { useAuth } from './use-auth';
import { useDiscoverContext } from './useDiscoverContext';

export interface DiscoverPreferences {
  topics: string[];
  beats: string[];
  verticals: string[];
  followed_brands: string[];
  saved_article_ids: string[];
  liked_article_ids: string[];
  dismissed_topics_panel: boolean;
  /** Daily digest opt-in for the active workspace. Written by `/api/discover/digest`, not the preferences POST. */
  digestEnabled: boolean;
}

const DEFAULT_PREFS: DiscoverPreferences = {
  topics: [],
  beats: [],
  verticals: [],
  followed_brands: [],
  saved_article_ids: [],
  liked_article_ids: [],
  dismissed_topics_panel: false,
  digestEnabled: false,
};
const ROOT_KEY = ['discover', 'preferences'] as const;
type Scope = { actorId: string; ownerId: string; brandKitId: string; revision: number };

export function discoverPreferencesQueryKey(
  actorId: string,
  ownerId: string,
  brandKitId: string,
  revision: number,
) {
  return [...ROOT_KEY, actorId, ownerId, brandKitId, revision] as const;
}

/** Thrown by the digest toggle so callers can tell "not available yet" from a failure. */
export class DiscoverDigestError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function requestDigest(token: string | null, brandKitId: string, enabled: boolean) {
  const response = await fetch('/api/discover/digest', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ brandKitId, enabled }),
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { code?: unknown } | null;
    throw new DiscoverDigestError(
      typeof body?.code === 'string' ? body.code : 'digest_update_failed',
      `Digest update failed (${response.status})`,
    );
  }
}

async function request(token: string | null, patch?: Partial<DiscoverPreferences>, scope?: Scope) {
  const response = await fetch('/api/discover/preferences', {
    method: patch ? 'POST' : 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(patch ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(patch ? { body: JSON.stringify(scope ? { ...patch, context: scope } : patch) } : {}),
  });
  if (!response.ok)
    throw new Error(`Preferences ${patch ? 'save' : 'fetch'} failed (${response.status})`);
  return (await response.json()) as DiscoverPreferences;
}

export function useDiscoverPreferences() {
  const { user, session } = useAuth();
  const { context, isLoading: contextLoading } = useDiscoverContext();
  const token = session?.access_token ?? null;
  const scope: Scope | null =
    user && context
      ? {
          actorId: user.id,
          ownerId: context.ownerId,
          brandKitId: context.brandKitId,
          revision: context.revision,
        }
      : null;
  const key = scope
    ? discoverPreferencesQueryKey(scope.actorId, scope.ownerId, scope.brandKitId, scope.revision)
    : discoverPreferencesQueryKey('anonymous', 'anonymous', 'anonymous', 0);
  const currentKeyRef = useRef<string>('');
  currentKeyRef.current = JSON.stringify(key);
  const sequenceRef = useRef(0);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: key,
    queryFn: () => request(token),
    enabled: !user || Boolean(scope),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const mutation = useMutation({
    mutationFn: ({
      patch,
      operationScope,
    }: {
      patch: Partial<DiscoverPreferences>;
      operationScope: Scope | null;
      sequence: number;
    }) => request(token, patch, operationScope ?? undefined),
    onMutate: async (variables) => {
      const operationKey = variables.operationScope
        ? discoverPreferencesQueryKey(
            variables.operationScope.actorId,
            variables.operationScope.ownerId,
            variables.operationScope.brandKitId,
            variables.operationScope.revision,
          )
        : discoverPreferencesQueryKey('anonymous', 'anonymous', 'anonymous', 0);
      await queryClient.cancelQueries({ queryKey: operationKey });
      const previous = queryClient.getQueryData<DiscoverPreferences>(operationKey);
      queryClient.setQueryData(operationKey, {
        ...(previous ?? DEFAULT_PREFS),
        ...variables.patch,
      });
      return { previous, operationKey };
    },
    onError: (_error, variables, state) => {
      if (variables.sequence === sequenceRef.current && state?.previous)
        queryClient.setQueryData(state.operationKey, state.previous);
    },
    onSuccess: (server, variables, state) => {
      if (
        variables.sequence === sequenceRef.current &&
        JSON.stringify(state?.operationKey) === currentKeyRef.current
      )
        queryClient.setQueryData(state!.operationKey, {
          ...server,
          // The preferences RPC does not return the digest flag; keep what we had.
          digestEnabled: server.digestEnabled ?? state?.previous?.digestEnabled ?? false,
        });
      /* Subjects and competitors decide what the feed returns; a saved change
         used to sit unseen until the next visit. */
      if ('beats' in variables.patch || 'followed_brands' in variables.patch) {
        void queryClient.invalidateQueries({ queryKey: ['discover', 'feed'] });
      }
    },
  });
  const digestMutation = useMutation({
    mutationFn: (variables: { brandKitId: string; enabled: boolean }) =>
      requestDigest(token, variables.brandKitId, variables.enabled),
  });
  /** Opt the active workspace in/out of the daily digest. Reverts the cache on failure. */
  const setDigestEnabled = (
    enabled: boolean,
    callbacks?: { onError?: (error: unknown) => void },
  ) => {
    if (!scope) return;
    const previous = queryClient.getQueryData<DiscoverPreferences>(key);
    queryClient.setQueryData(key, { ...(previous ?? DEFAULT_PREFS), digestEnabled: enabled });
    digestMutation.mutate(
      { brandKitId: scope.brandKitId, enabled },
      {
        onError: (error) => {
          queryClient.setQueryData(key, {
            ...(queryClient.getQueryData<DiscoverPreferences>(key) ?? DEFAULT_PREFS),
            digestEnabled: previous?.digestEnabled ?? false,
          });
          callbacks?.onError?.(error);
        },
      },
    );
  };
  return {
    setDigestEnabled,
    preferences: query.data ?? DEFAULT_PREFS,
    isLoading: query.isLoading || (Boolean(user) && contextLoading),
    /* The callbacks used to be dropped: ArticleCard passes onSuccess/onError so
       "Keep for later" can confirm or report the write, and a one-argument
       signature silently swallowed both — the button gave no feedback at all. */
    update: (
      patch: Partial<DiscoverPreferences>,
      callbacks?: { onSuccess?: () => void; onError?: (error: unknown) => void },
    ) =>
      mutation.mutate(
        { patch, operationScope: scope, sequence: ++sequenceRef.current },
        {
          onSuccess: () => callbacks?.onSuccess?.(),
          onError: (error) => callbacks?.onError?.(error),
        },
      ),
    isUpdating: mutation.isPending,
  };
}
